const CONSTANTS = (module.exports = {});

/**
 * roles-permissions-2.0 P5 (docs/roles-2.0/SPEC.md §2-3, DECISIONS.md D-1/D-2).
 *
 * Departments are HARD-CODED here, not editable at runtime (D-2) — the admin
 * screen reads what a department grants; it never writes it. Each id is the
 * exact `departments[]` value stored on a backoffice_user (packages/mongodb
 * lib/model/backoffice_user.js) and the exact key `services/trader-api/lib/
 * department-capability-sets.js` binds a capability bundle to.
 *
 * The six departments below are SPEC.md §2's model table verbatim. Sovereign
 * is deliberately NOT a department (SPEC §2: "boolean. 2 accounts only. Not
 * a department") — it is its own top-level field on the schema.
 */
CONSTANTS.BACKOFFICE_DEPARTMENTS = {
    CUSTOMER_SUCCESS: 'cs', // SPEC §3: union of reservations_manager's live routes (55).
    SALES_AND_ACCOUNTS: 'sales', // SPEC §3: union of account_manager's live routes (34). NOT the `sales` role — see D-15.
    FINANCE: 'finance', // SPEC §3: union of finance_manager's live routes (13).
    SUPPLY_AND_CONTENT: 'supply', // SPEC §3.4/D-13: matcher's 2 live routes + 5 admin-only catalogue routes + 2 shared reads already held by CS/Sales.
    PRICING: 'pricing', // SPEC §3.3/D-8/D-4: global margin config, admin-only today, moved into its own department.
    PLATFORM: 'platform', // SPEC §3.5: 6 admin-only routes, deliberately excludes destruction, staff-account writes and pricing/supply.
};

/**
 * SPEC.md §2/§4. `execute` performs actions; `oversight` strips every write
 * (SPEC §4.1: keyed off route CLASS, not HTTP verb — several semantic reads
 * are bound as POST).
 */
CONSTANTS.BACKOFFICE_USER_MODES = {
    EXECUTE: 'execute',
    OVERSIGHT: 'oversight',
};

/**
 * SPEC.md §2/§3.6/§3.8. `manage_team` is DELIBERATELY excluded from this
 * vocabulary even though SPEC §2's model table lists it as a fifth add-on
 * value — see packages/mongodb/lib/model/backoffice_user.js's own comment
 * for why it is a separate top-level boolean field instead (H-1, D-7, and
 * the P5 dispatch brief that authored this file all treat it as a distinct
 * dial, scoped and audited separately in Wave 2 — folding it into a
 * same-shaped string array as these four would have let it slip through
 * the generic "hold an addon → gain its capabilities" path this file's
 * sibling module (department-capability-sets.js) implements for the other
 * four, before Wave 2's subset-scoping and audit trail exist). Flagged for
 * product/eng sign-off rather than resolved silently.
 */
/**
 * CORRECTED post-P5 (human ruling, roles-permissions-2.0 correction round):
 * `export` used to be ONE flat add-on covering all 4 export-classed
 * capabilities. Migration (role-department-migration.js) grants an add-on
 * wholesale, so a single flat `export` necessarily handed RESERVATIONS_MANAGER
 * and ACCOUNT_MANAGER each other's export capabilities too — a real widening
 * of PII/financial egress introduced BY the migration, not present in either
 * role's live access today. Split per export domain so migration can grant
 * exactly the domains a role already holds and nothing more. See
 * `lib/department-capability-sets.js`'s ADDON_CAPABILITIES header for the
 * per-domain capability membership and `lib/role-department-migration.js`
 * for the corrected, now-lossless-in-both-directions per-role grants.
 */
CONSTANTS.BACKOFFICE_USER_ADDONS = {
    EXPORT_HOTELS: 'export_hotels', // hotels.export.providers_csv.
    EXPORT_PAYMENTS: 'export_payments', // money.payments.export.
    EXPORT_RESERVATIONS: 'export_reservations', // reservations.export.csv.
    EXPORT_USERS: 'export_users', // users.export.csv.
    IMPERSONATE: 'impersonate', // SPEC §3.6: POST /arbitrip/users/generate_login_link (today ADMIN, AUTH0_IMPERSONATOR_LOGIN).
    TEMPLATES: 'templates', // SPEC §3.6: POST /arbitrip/company_templates (today ADMIN, TEMPLATE_MANAGER).
    SALES_DASHBOARD: 'sales_dashboard', // SPEC §3.6/D-15: GET /arbitrip/stats/sales/leaderboard — the `sales` role's one route.
    // roles-permissions-2.0 (team-lead ruling, 2026-09-03) — the one
    // capability the department model had no home for at all:
    // `platform.trace_logs.view` (GET /trace-logs/user-flow, arrived with
    // release/62.3). An ADD-ON, not a department entry, because the route
    // reads the four PRODUCTION CloudWatch groups in routes/trace-logs.js's
    // LOG_GROUP_MAP and production log lines are known to carry credentials
    // and traveller personal data — the same "data leaves the building"
    // shape as the four export add-ons above, and the same grant-only
    // shape: no department bundle grants an add-on, so holding it is always
    // an explicit per-account decision. Deliberately NOT the PLATFORM
    // department (that bundle is operational — AI warm controls, S3 upload
    // — and every account an administrator puts in it would inherit
    // production log access), and deliberately NOT
    // SOVEREIGN_EXTRA_CAPABILITIES (see the SOVEREIGN ACCOUNT POPULATION
    // block in services/trader-api/middleware/role-auth.js for the figure
    // and its date — do not restate it here; sovereign-only would collapse
    // this onto that population with no way to delegate, the
    // `governance.investigate` shape this cutover exists to avoid).
    TRACE_LOGS: 'trace_logs', // platform.trace_logs.view.
};
